import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Field, inputCls } from "@/components/ui-primitives";

const empty = { site_id: "", name: "", customer: "", address: "", contact: "", phone: "" };

export default function Locations() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const { data } = await api.get("/locations");
      setItems(data);
    } catch {
      toast.error("Failed to load");
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.site_id.trim() || !form.name.trim()) {
      toast.error("Site ID and Name are required");
      return;
    }
    try {
      await api.post("/locations", form);
      toast.success("Location added");
      setForm(empty);
      load();
    } catch {
      toast.error("Add failed");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/locations/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="p-8 max-w-[1800px] mx-auto">
      <PageHeader eyebrow="Sites" title="Locations" accent="Roster" />

      <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 bg-[#121212] border border-white/10 p-5 mb-6">
        <Field label="Site ID *">
          <input data-testid="loc-siteid" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Location Name *">
          <input data-testid="loc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Customer">
          <input data-testid="loc-customer" value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Address">
          <input data-testid="loc-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Contact">
          <input data-testid="loc-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Phone">
          <input data-testid="loc-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
        </Field>
        <div className="flex items-end lg:col-span-2">
          <button
            data-testid="loc-add-btn"
            type="submit"
            className="w-full px-5 py-2.5 bg-[#007AFF] hover:bg-[#3395FF] text-white text-sm font-medium"
          >
            Add Location
          </button>
        </div>
      </form>

      <div className="border border-white/10 bg-[#121212] overflow-x-auto">
        <table data-testid="loc-table" className="w-full text-sm">
          <thead className="bg-black/60 backdrop-blur-xl">
            <tr className="text-left font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373]">
              <th className="px-4 py-3 border-b border-white/10">Site ID</th>
              <th className="px-4 py-3 border-b border-white/10">Name</th>
              <th className="px-4 py-3 border-b border-white/10">Customer</th>
              <th className="px-4 py-3 border-b border-white/10">Address</th>
              <th className="px-4 py-3 border-b border-white/10">Contact</th>
              <th className="px-4 py-3 border-b border-white/10">Phone</th>
              <th className="px-4 py-3 border-b border-white/10 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-[#A3A3A3]">No locations yet.</td></tr>
            )}
            {items.map((l) => (
              <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3 font-mono-tab text-xs text-[#E0E0E0]">{l.site_id}</td>
                <td className="px-4 py-3 text-white font-medium">{l.name}</td>
                <td className="px-4 py-3 text-[#E0E0E0]">{l.customer || "—"}</td>
                <td className="px-4 py-3 text-[#E0E0E0]">{l.address || "—"}</td>
                <td className="px-4 py-3 text-[#E0E0E0]">{l.contact || "—"}</td>
                <td className="px-4 py-3 text-[#E0E0E0]">{l.phone || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end">
                    <button
                      data-testid={`loc-del-${l.id}`}
                      onClick={() => remove(l.id, l.name)}
                      className="p-1.5 border border-white/10 hover:border-[#FF3B30]/40 hover:bg-[#FF3B30]/10"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-[#FF3B30]" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
